import React, { useState, useMemo, useCallback } from 'react';
import { Box, Typography, Table, Loader, Badge } from '@lucid/lego';
import { useStore } from '../../../store/store';
import { useGetAlerts } from '../../../api/services/lupp/fhm-alerts/fhm.alerts';
import type { FleetHealthAlert } from '../../../api/services/lupp/fhm-alerts/dto.fhm.alerts';
import { formatStatus, getPriorityBadgeColor, getStatusBadgeColor } from './utils';
import { formatDate } from '../../../utils/tools';
import AlertDetails from './AlertDetails/AlertDetails';
import styles from './FleetHealth.module.scss';

const FleetHealth: React.FC = () => {
  const theme = useStore((state) => state.theme);
  const [isSliderOpen, setIsSliderOpen] = useState(false);
  const [selectedAlertId, setSelectedAlertId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(10);
  const [sortField] = useState('createdAt,desc'); // Default sort

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
    setCurrentPage(0); // Reset to first page when search changes
  }, []);

  // Use server-side search/sort/pagination
  const {
    data: alertsResponse,
    isLoading,
    isError,
    error,
  } = useGetAlerts({
    page: currentPage,
    size: pageSize,
    searchString: searchQuery,
    sort: sortField,
  });

  const alerts = alertsResponse?.items || [];
  const totalCount = alertsResponse?.totalCount || 0;

  const handleViewAlert = useCallback((alertId: string) => {
    setSelectedAlertId(alertId);
    setIsSliderOpen(true);
  }, []);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const tableColumns = useMemo(
    () => [
      {
        key: 'vin',
        title: 'VIN',
        width: '180px',
        sortable: true,
        render: (_value: unknown, alert: FleetHealthAlert) => {
          if (!alert) return null;
          return (
            <Typography
              variant="body"
              size="small"
              color="blue"
              theme={theme}
              className={styles.alertLink}
              onClick={() => handleViewAlert(alert.id)}
              data-testid={`alert-link-${alert.id}`}
            >
              {alert.vin || '-'}
            </Typography>
          );
        },
      },
      {
        key: 'subject',
        title: 'Alert Name',
        width: '250px',
        sortable: true,
        render: (_value: unknown, alert: FleetHealthAlert) => (
          <Typography variant="body" size="small" color="black" theme={theme}>
            {alert?.subject || alert?.message || '-'}
          </Typography>
        ),
      },
      {
        key: 'severity',
        title: 'Priority',
        width: '120px',
        sortable: true,
        render: (_value: unknown, alert: FleetHealthAlert) => {
          if (!alert?.severity) return null;
          return (
            <Badge
              label={alert.severity}
              color={getPriorityBadgeColor(alert.severity)}
              type="secondary"
              theme={theme}
            />
          );
        },
      },
      {
        key: 'status',
        title: 'Status',
        width: '120px',
        sortable: true,
        render: (_value: unknown, alert: FleetHealthAlert) => {
          if (!alert?.status) return null;
          return (
            <Badge
              label={formatStatus(alert.status)}
              color={getStatusBadgeColor(alert.status)}
              type="secondary"
              theme={theme}
            />
          );
        },
      },
      {
        key: 'createdAt',
        title: 'Date Created',
        width: '140px',
        sortable: true,
        render: (_value: unknown, alert: FleetHealthAlert) => (
          <Typography variant="body" size="small" color="black" theme={theme}>
            {alert?.createdAt ? formatDate(alert.createdAt) : '-'}
          </Typography>
        ),
      },
    ],
    [theme, handleViewAlert]
  );

  return (
    <Box>
      <Box className={styles.header}>
        <Typography variant="headline" size="small" color="primary-1" theme={theme}>
          Fleet Health Monitoring Alerts
        </Typography>
        <Box mt={8}>
          <Typography variant="body" size="medium" color="gray-3" theme={theme}>
            View and manage fleet health monitoring alerts for your vehicles.
          </Typography>
        </Box>
      </Box>

      <Box pt={24}>
        {isLoading && (
          <Box className={styles.loadingContainer}>
            <Loader size={48} />
          </Box>
        )}

        {isError && (
          <Box className={styles.errorContainer}>
            <Typography variant="body" size="medium" color="error-1" theme={theme}>
              Error loading alerts: {error instanceof Error ? error.message : 'Unknown error'}
            </Typography>
          </Box>
        )}

        {!isLoading && !isError && (
          <>
            <Table
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              columns={tableColumns as any}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              data={alerts as any}
              theme={theme === 'dark' ? 'light' : 'dark'}
              pagination={{
                enabled: true,
                total: totalCount,
                current: currentPage,
                pageSize: pageSize,
                onChange: (page) => handlePageChange(page),
              }}
              search={{
                placeholder: 'Search alerts by VIN or status...',
                value: searchQuery,
                onChange: handleSearchChange,
              }}
              emptyState={{
                message: searchQuery.trim()
                  ? 'No results found! Please update your query'
                  : 'No alerts available',
              }}
              gaComponent="fleet-health-alerts-table"
              dataTestId="fleet-health-alerts-table"
            />
            <AlertDetails
              theme={theme}
              isOpen={isSliderOpen}
              onClose={() => {
                setIsSliderOpen(false);
                setSelectedAlertId(null);
              }}
              alertId={selectedAlertId || ''}
            />
          </>
        )}
      </Box>
    </Box>
  );
};

export default FleetHealth;
